import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { LeadSummaryRow, ReportBucket, ReportFilters, ReportGroupBy } from '../../../core/models/report.model';
import { PermissionService } from '../../../core/services/permission.service';
import { ReportsService } from '../../../core/services/reports.service';
import { TranslationService } from '../../../core/services/translation.service';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CardActionsComponent } from '../../../shared/components/card/card-actions.component';
import { CardContentComponent } from '../../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../../shared/components/card/card-title.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ReportFilterBarComponent } from '../../../shared/components/report-filter-bar/report-filter-bar.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { exportToCSV, exportToXLSX, generateFilename } from '../../../shared/utils/export.utils';

interface BucketGroupModel {
  bucket: ReportBucket;
  groupBy: ReportGroupBy;
}

/** "Leads by day/week/user/team/tag, with value" (design doc §8). */
@Component({
  selector: 'app-lead-summary',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    ButtonComponent,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    CardActionsComponent,
    IconComponent,
    ReportFilterBarComponent,
    SpinnerComponent,
    TranslatePipe,
  ],
  templateUrl: './lead-summary.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./lead-summary.component.css', '../../../../css/report-print.css'],
})
export class LeadSummaryComponent {
  private reportsService = inject(ReportsService);
  private permissionService = inject(PermissionService);
  private translate = inject(TranslationService);

  includeAllUsers = this.permissionService.has('interaction.read-others');
  canExport = this.permissionService.has('report.export');

  rows = signal<LeadSummaryRow[]>([]);
  loading = signal(false);
  private lastFilters: ReportFilters | null = null;

  bucketGroupForm = new FormGroup({});
  bucketGroupModel: BucketGroupModel = { bucket: 'day', groupBy: 'user' };
  bucketGroupFields: FormlyFieldConfig[] = [
    {
      key: 'bucket',
      type: 'native-select',
      props: {
        label: this.translate.translate('reports.rateTrends.bucket'),
        showLabel: true,
        options: [
          { value: 'day', label: this.translate.translate('reports.rateTrends.day') },
          { value: 'week', label: this.translate.translate('reports.rateTrends.week') },
          { value: 'month', label: this.translate.translate('reports.rateTrends.month') },
        ],
      },
    },
    {
      key: 'groupBy',
      type: 'native-select',
      props: {
        label: this.translate.translate('reports.rateTrends.groupBy'),
        showLabel: true,
        options: [
          { value: 'user', label: this.translate.translate('reports.rateTrends.byUser') },
          { value: 'team', label: this.translate.translate('reports.rateTrends.byTeam') },
          { value: 'tag', label: this.translate.translate('reports.rateTrends.byTag') },
        ],
      },
    },
  ];

  constructor() {
    this.bucketGroupForm.valueChanges.subscribe(() => this.reload());
  }

  async onFiltersChange(filters: ReportFilters): Promise<void> {
    this.lastFilters = filters;
    await this.reload();
  }

  private async reload(): Promise<void> {
    if (!this.lastFilters) return;
    this.loading.set(true);
    try {
      this.rows.set(
        await this.reportsService.getLeadSummary(this.lastFilters, this.includeAllUsers, this.bucketGroupModel.bucket, this.bucketGroupModel.groupBy),
      );
    } finally {
      this.loading.set(false);
    }
  }

  exportCsv(): void {
    exportToCSV(this.exportRows(), generateFilename('lead-summary'));
  }

  exportXlsx(): void {
    exportToXLSX(this.exportRows(), generateFilename('lead-summary'), 'Lead Summary');
  }

  private exportRows(): Record<string, string | number>[] {
    return this.rows().map((r) => ({
      Date: r.bucketLabel,
      Group: r.groupLabel,
      Leads: r.leadCount,
      'Total value': r.totalValue,
      Won: r.wonCount,
      Lost: r.lostCount,
    }));
  }
}
